import axios from 'axios';
import apiClient from '../apiClient';

// Get the base API URL from the existing apiClient configuration
const API_URL = apiClient.defaults.baseURL;

console.log('Creating admin API client with base URL:', API_URL);

// Create axios instance with credentials support for admin routes
// Inherits the base URL from apiClient, which uses VITE_API_URL
const adminApi = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Enable cookies for session-based auth
  headers: {
    'Content-Type': 'application/json',
  }
});

// Authentication APIs
export const loginAdmin = async (username: string, password: string) => {
  try {
    const response = await adminApi.post('/admin/login', { username, password });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const logoutAdmin = async () => {
  try {
    const response = await adminApi.post('/admin/logout');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const checkAuthStatus = async () => {
  try {
    const response = await adminApi.get('/admin/auth');
    return response.data;
  } catch (error) {
    return { authenticated: false };
  }
};

// Dashboard data APIs
export const getDashboardStats = async (timeRange: string = '7d') => {
  try {
    const response = await adminApi.get(`/admin/dashboard?time_range=${timeRange}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getPersonaStats = async (timeRange: string = '7d') => {
  try {
    const response = await adminApi.get(`/admin/personas/stats?time_range=${timeRange}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getPersonaUsage = async () => {
  try {
    const response = await adminApi.get('/admin/personas/usage');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getPersonaDetail = async (personaName: string) => {
  try {
    const response = await adminApi.get(`/admin/personas/${encodeURIComponent(personaName)}/detail`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCostData = async (timeRange: string = '7d') => {
  try {
    const response = await adminApi.get(`/admin/costs?time_range=${timeRange}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Note: System/hardware stats removed - use Digital Ocean monitoring dashboard
// export const getSystemData = async () => { ... }

export const getResearchStats = async (timeRange: string = '7d') => {
  try {
    const response = await adminApi.get(`/admin/research?time_range=${timeRange}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Chat analytics APIs
export const getChatSummaries = async (timeRange: string = '7d', limit: number = 10) => {
  try {
    const response = await adminApi.get(`/admin/chat/summaries?time_range=${timeRange}&limit=${limit}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getChatsByDate = async (timeRange: string = '7d', page: number = 1, pageSize: number = 20) => {
  try {
    const response = await adminApi.get('/admin/chat/by-date', {
      params: { time_range: timeRange, page, page_size: pageSize }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getChatDetail = async (chatId: string) => {
  try {
    const response = await adminApi.get(`/admin/chat/${chatId}/detail`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getChatMessages = async (chatId: string) => {
  try {
    const response = await adminApi.get(`/admin/chat/${chatId}/messages`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Questions admin API
export interface AdminQuestion {
  id: string;
  date: string;
  persona_name: string;
  raw_question: string;
  refined_question: string | null;
  status: string;
  created_at: string;
}

export const getAdminQuestions = async (status?: string): Promise<AdminQuestion[]> => {
  try {
    const params = status ? { status } : {};
    const response = await adminApi.get('/admin/questions', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAdminQuestionDetail = async (qid: string): Promise<AdminQuestion> => {
  try {
    const response = await adminApi.get(`/admin/questions/${qid}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Email threads admin API
export interface AdminEmailThread {
  id: string;
  user_email: string;
  question_id: string;
  persona_name: string;
  phase: string;
  processed_by_conductor: number;
  created_at: string;
  last_reply_at: string | null;
}

export interface AdminEmailMessage {
  id: string;
  thread_id: string;
  direction: string;
  content: string;
  sent_at: string;
}

export interface AdminEmailThreadDetail extends AdminEmailThread {
  messages: AdminEmailMessage[];
  change_intent: string | null;
}

export const getAdminEmailThreads = async (phase?: string): Promise<AdminEmailThread[]> => {
  try {
    const params = phase ? { phase } : {};
    const response = await adminApi.get('/admin/email-threads', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAdminEmailThreadDetail = async (
  threadId: string
): Promise<AdminEmailThreadDetail> => {
  try {
    const response = await adminApi.get(`/admin/email-threads/${threadId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export default adminApi;
